import { useCallback, useEffect, useMemo, useReducer } from 'react'
import { createContext, useContextSelector } from 'use-context-selector'
export type PreferencesState = {
  enableDevtools: boolean
  autoOpenFirstTimeWelcome: boolean
}

export type PreferencesActions = {
  setAutoOpenFirstTimeWelcome: (enabled: boolean) => void
  setEnableDevtools: (enabled: boolean) => void
}

type PreferencesAction =
  | { type: 'setAutoOpenFirstTimeWelcome'; enabled: boolean }
  | { type: 'setEnableDevtools'; enabled: boolean }

export const PREFERENCES_STORAGE_KEY = 'floodnet.settings.v1'

export const DEFAULT_PREFERENCES_STATE: PreferencesState = {
  enableDevtools: import.meta.env.DEV,
  autoOpenFirstTimeWelcome: true,
}

const fallbacks = <T,>(...values: (T | undefined)[]): T | undefined => values.find((value) => value !== undefined)

export function validatePreferences(parsed: Partial<PreferencesState>): Partial<PreferencesState> {
  const legacy = parsed as Partial<PreferencesState> & {
    featureFlags?: Record<string, unknown>
    general?: Record<string, unknown>
  }

  return {
    enableDevtools: fallbacks(parsed.enableDevtools, legacy.featureFlags?.enableDevtools, DEFAULT_PREFERENCES_STATE.enableDevtools) as boolean | undefined,
    autoOpenFirstTimeWelcome: fallbacks(
      parsed.autoOpenFirstTimeWelcome,
      legacy.general?.autoOpenFirstTimeWelcome,
      legacy.general?.autoOpenFirstTimeTour,
      DEFAULT_PREFERENCES_STATE.autoOpenFirstTimeWelcome,
    ) as boolean | undefined,
  }
}

export function normalizePreferences(parsed: Partial<PreferencesState> = {}): PreferencesState {
  const validated = validatePreferences(parsed)
  return {
    ...DEFAULT_PREFERENCES_STATE,
    ...validated,
  }
}

export function preferencesReducer(state: PreferencesState, action: PreferencesAction): PreferencesState {
  switch (action.type) {
    case 'setAutoOpenFirstTimeWelcome':
      if (state.autoOpenFirstTimeWelcome === action.enabled) return state
      return { ...state, autoOpenFirstTimeWelcome: action.enabled }
    case 'setEnableDevtools':
      if (state.enableDevtools === action.enabled) return state
      return { ...state, enableDevtools: action.enabled }
  }
}

const readInitialPreferences = (): PreferencesState => {
  try {
    const raw = window.localStorage.getItem(PREFERENCES_STORAGE_KEY)
    return normalizePreferences(raw ? JSON.parse(raw) as Partial<PreferencesState> : {})
  } catch {
    return normalizePreferences()
  }
}

type PreferencesContextValue = {
  state: PreferencesState
  actions: PreferencesActions
}

const PreferencesContext = createContext<PreferencesContextValue | undefined>(undefined)

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(preferencesReducer, undefined, readInitialPreferences)

  useEffect(() => {
    try {
      window.localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(state))
    } catch {
      // ignore storage errors
    }
  }, [state])

  const setAutoOpenFirstTimeWelcome = useCallback((enabled: boolean) => {
    dispatch({ type: 'setAutoOpenFirstTimeWelcome', enabled })
  }, [])

  const setEnableDevtools = useCallback((enabled: boolean) => {
    dispatch({ type: 'setEnableDevtools', enabled })
  }, [])

  const actions = useMemo<PreferencesActions>(() => ({
    setAutoOpenFirstTimeWelcome,
    setEnableDevtools,
  }), [setAutoOpenFirstTimeWelcome, setEnableDevtools])

  const value = useMemo<PreferencesContextValue>(() => ({
    state,
    actions,
  }), [actions, state])

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>
}

const usePreferencesSelector = <T,>(selector: (value: PreferencesContextValue) => T, name: string): T => {
  const selected = useContextSelector(PreferencesContext, (value) => value ? selector(value) : undefined)
  if (selected === undefined) {
    throw new Error(`${name} must be used within a PreferencesProvider`)
  }
  return selected
}

export const usePreferencesState = () => usePreferencesSelector((value) => value.state, 'usePreferencesState')
export const usePreferencesActions = () => usePreferencesSelector((value) => value.actions, 'usePreferencesActions')
export const useEnableDevtools = () => usePreferencesSelector((value) => value.state.enableDevtools, 'useEnableDevtools')
export const useAutoOpenFirstTimeWelcome = () => usePreferencesSelector((value) => value.state.autoOpenFirstTimeWelcome, 'useAutoOpenFirstTimeWelcome')
